// Batched, cached fetcher for per-document preview SAS URLs.
// Memory only. URLs are never logged.
import api from "@/lib/api";

const MAX_PER_REQUEST = 50;
const REFRESH_MARGIN_MS = 60 * 1000;
const MISS_TTL_MS = 5 * 60 * 1000; // don't re-ask for a path the server skipped

// One batching engine for both endpoints: calls made in the same tick become one POST
// (chunks of 50), results are cached in memory until shortly before they expire.
function createBatchedFetcher({ endpoint, listKey, toEntry, cacheMisses = false }) {
  const cache = new Map(); // blobPath -> entry | { miss: true, at }
  const inflight = new Map(); // blobPath -> Promise
  let queue = []; // [{ blobPath, resolve }]
  let scheduled = false;

  function fresh(entry) {
    if (!entry) return false;
    if (entry.miss) return Date.now() - entry.at < MISS_TTL_MS;
    if (!entry.expires_at) return false;
    return new Date(entry.expires_at).getTime() - REFRESH_MARGIN_MS > Date.now();
  }

  async function flush() {
    scheduled = false;
    const batch = queue;
    queue = [];
    const paths = [...new Set(batch.map((b) => b.blobPath))];
    const results = new Map();
    const answered = new Set();
    for (let i = 0; i < paths.length; i += MAX_PER_REQUEST) {
      const chunk = paths.slice(i, i + MAX_PER_REQUEST);
      try {
        const resp = await api.post(endpoint, { blob_paths: chunk });
        for (const u of resp?.data?.[listKey] || []) results.set(u.blob_path, u);
        chunk.forEach((p) => answered.add(p));
      } catch {
        // chunk stays unresolved -> null
      }
    }
    for (const { blobPath, resolve } of batch) {
      const u = results.get(blobPath);
      if (u) {
        const entry = toEntry(u);
        cache.set(blobPath, entry);
        resolve(entry);
      } else {
        if (cacheMisses && answered.has(blobPath)) cache.set(blobPath, { miss: true, at: Date.now() });
        resolve(null);
      }
    }
  }

  function get(blobPath) {
    const hit = cache.get(blobPath);
    if (hit && fresh(hit)) return Promise.resolve(hit.miss ? null : hit);
    if (inflight.has(blobPath)) return inflight.get(blobPath);
    const p = new Promise((resolve) => {
      queue.push({ blobPath, resolve });
      if (!scheduled) {
        scheduled = true;
        setTimeout(flush, 0);
      }
    }).finally(() => inflight.delete(blobPath));
    inflight.set(blobPath, p);
    return p;
  }

  return {
    get,
    prefetch: (blobPaths) => Promise.all([...new Set(blobPaths.filter(Boolean))].map(get)),
    invalidate: (blobPath) => cache.delete(blobPath),
    reset: () => {
      cache.clear();
      inflight.clear();
      queue = [];
      scheduled = false;
    },
  };
}

const previews = createBatchedFetcher({
  endpoint: "/documents/preview-urls",
  listKey: "urls",
  toEntry: (u) => ({
    url: u.url,
    download_url: u.download_url,
    filename: u.filename,
    expires_at: u.expires_at,
  }),
});

// Server-made page-1 JPEG thumbnails (null when skipped / not a PDF / render failed).
const thumbs = createBatchedFetcher({
  endpoint: "/documents/thumbs",
  listKey: "thumbs",
  toEntry: (u) => ({ url: u.url, expires_at: u.expires_at }),
  cacheMisses: true,
});

export const getPreviewUrl = previews.get;

// Warm the cache for a whole page of rows: one POST per 50 paths instead of one per thumbnail.
export const prefetchPreviewUrls = previews.prefetch;

export const getThumbUrl = thumbs.get;
export const prefetchThumbUrls = thumbs.prefetch;

export function invalidate(blobPath) {
  previews.invalidate(blobPath);
}

export function _resetPreviewUrlsForTests() {
  previews.reset();
  thumbs.reset();
}
